import React, {
  useEffect,
  useRef,
  useState
} from 'react';

import type {
  RealtimeChannel
} from '@supabase/supabase-js';

import {
  Expand,
  Loader2,
  MonitorUp,
  Play,
  Power,
  Radio,
  ShieldCheck,
  Wifi
} from 'lucide-react';

import {
  supabase
} from '../lib/supabase';

import {
  useRealtime
} from '../contexts/RealtimeContext';


type RemoteSession = {

  sessionId:
    string;

  viewerUrl:
    string;

  tunnelUrl:
    string;

  width:
    number;

  height:
    number;

  fps:
    number;

  mode:
    'view-only';

  startedAt:
    string;
};


type PendingRequest = {

  resolve:
    (
      result:
        any
    ) => void;

  reject:
    (
      error:
        Error
    ) => void;

  timer:
    ReturnType<
      typeof setTimeout
    >;
};


export const RemoteDesktopPage:
React.FC = () => {

  const {
    device,
    deviceStatus,
    latencyMs
  } =
    useRealtime();


  const [
    session,
    setSession
  ] =
    useState<
      RemoteSession |
      null
    >(
      null
    );


  const [
    actionState,
    setActionState
  ] =
    useState<
      'idle' |
      'starting' |
      'stopping'
    >(
      'idle'
    );


  const [
    channelReady,
    setChannelReady
  ] =
    useState(
      false
    );


  const [
    streamConnected,
    setStreamConnected
  ] =
    useState(
      false
    );


  const [
    displayFps,
    setDisplayFps
  ] =
    useState(
      0
    );


  const [
    error,
    setError
  ] =
    useState<
      string |
      null
    >(
      null
    );


  const channelRef =
    useRef<
      RealtimeChannel |
      null
    >(
      null
    );


  const viewerRef =
    useRef<
      HTMLDivElement |
      null
    >(
      null
    );


  const imageRef =
    useRef<
      HTMLImageElement |
      null
    >(
      null
    );


  const websocketRef =
    useRef<
      WebSocket |
      null
    >(
      null
    );


  const previousFrameUrlRef =
    useRef<
      string |
      null
    >(
      null
    );


  const frameCounterRef =
    useRef(
      0
    );


  const frameTimerRef =
    useRef(
      performance.now()
    );


  const pendingStarts =
    useRef<
      Map<
        string,
        PendingRequest
      >
    >(
      new Map()
    );


  const pendingStops =
    useRef<
      Map<
        string,
        PendingRequest
      >
    >(
      new Map()
    );


  const online =
    deviceStatus ===
      'online';


  const busy =
    actionState !==
      'idle';


  // =========================================================
  // CONTROL CHANNEL
  // =========================================================

  useEffect(
    () => {

      if (
        !device?.id
      ) {

        setChannelReady(
          false
        );

        return;
      }


      let disposed =
        false;


      const channel =
        supabase.channel(
          `device:${device.id}`,
          {
            config: {

              private:
                true,

              broadcast: {
                ack:
                  true
              }
            }
          }
        );


      channel.on(
        'broadcast',
        {
          event:
            'remote_desktop_start_result'
        },
        (
          {
            payload
          }:
          {
            payload:
              any
          }
        ) => {

          const result =
            payload?.payload;


          const requestId =
            result?.requestId;


          if (
            !requestId
          ) {

            return;
          }


          const pending =
            pendingStarts.current.get(
              requestId
            );


          if (
            !pending
          ) {

            return;
          }


          clearTimeout(
            pending.timer
          );


          pendingStarts.current.delete(
            requestId
          );


          pending.resolve(
            result
          );
        }
      );


      channel.on(
        'broadcast',
        {
          event:
            'remote_desktop_stop_result'
        },
        (
          {
            payload
          }:
          {
            payload:
              any
          }
        ) => {

          const result =
            payload?.payload;


          const requestId =
            result?.requestId;


          if (
            !requestId
          ) {

            return;
          }


          const pending =
            pendingStops.current.get(
              requestId
            );


          if (
            !pending
          ) {

            return;
          }


          clearTimeout(
            pending.timer
          );


          pendingStops.current.delete(
            requestId
          );


          pending.resolve(
            result
          );
        }
      );


      channel.subscribe(
        status => {

          if (
            disposed
          ) {

            return;
          }


          if (
            status ===
              'SUBSCRIBED'
          ) {

            channelRef.current =
              channel;


            setChannelReady(
              true
            );


            return;
          }


          if (
            status ===
              'CHANNEL_ERROR' ||
            status ===
              'TIMED_OUT' ||
            status ===
              'CLOSED'
          ) {

            setChannelReady(
              false
            );
          }
        }
      );


      return () => {

        disposed =
          true;


        if (
          channelRef.current ===
            channel
        ) {

          channelRef.current =
            null;
        }


        setChannelReady(
          false
        );


        for (
          const [
            id,
            pending
          ]
          of pendingStarts.current
        ) {

          clearTimeout(
            pending.timer
          );


          pending.reject(
            new Error(
              'Remote Desktop control channel closed'
            )
          );


          pendingStarts.current.delete(
            id
          );
        }


        for (
          const [
            id,
            pending
          ]
          of pendingStops.current
        ) {

          clearTimeout(
            pending.timer
          );


          pending.reject(
            new Error(
              'Remote Desktop control channel closed'
            )
          );


          pendingStops.current.delete(
            id
          );
        }


        void channel.unsubscribe();
      };
    },
    [
      device?.id
    ]
  );


  // =========================================================
  // LIVE BINARY STREAM
  //
  // Cloudflare Quick Tunnel may announce its hostname before
  // the public WebSocket edge is ready. Mobile connections may
  // also briefly change state.
  //
  // Therefore this viewer MUST reconnect rather than treating
  // the first failed handshake as a permanent session failure.
  // =========================================================

  useEffect(
    () => {

      setStreamConnected(
        false
      );


      setDisplayFps(
        0
      );


      frameCounterRef.current =
        0;


      frameTimerRef.current =
        performance.now();


      let disposed =
        false;


      let retryTimer:
        ReturnType<
          typeof setTimeout
        > |
        null =
        null;


      let consecutiveFailures =
        0;


      const maxInitialFailures =
        18;


      const existingSocket =
        websocketRef.current;


      if (
        existingSocket
      ) {

        websocketRef.current =
          null;


        try {

          existingSocket.close();

        }
        catch {}
      }


      if (
        previousFrameUrlRef.current
      ) {

        URL.revokeObjectURL(
          previousFrameUrlRef.current
        );


        previousFrameUrlRef.current =
          null;
      }


      if (
        !session
      ) {

        return;
      }


      let sessionUrl:
        URL;


      try {

        sessionUrl =
          new URL(
            session.viewerUrl
          );

      }
      catch {

        setError(
          'Remote Desktop session URL is invalid.'
        );

        return;
      }


      const fragment =
        new URLSearchParams(
          sessionUrl.hash.slice(
            1
          )
        );


      const token =
        fragment.get(
          'token'
        );


      if (
        !token
      ) {

        setError(
          'Remote Desktop session token is missing.'
        );

        return;
      }


      const protocol =
        sessionUrl.protocol ===
          'https:'
          ? 'wss:'
          : 'ws:';


      const socketUrl =
        `${protocol}//${sessionUrl.host}/ws?token=${encodeURIComponent(token)}`;


      const scheduleReconnect =
        () => {

          if (
            disposed
          ) {

            return;
          }


          consecutiveFailures++;


          if (
            consecutiveFailures >=
              maxInitialFailures
          ) {

            setError(
              'Live desktop transport could not connect after repeated attempts.'
            );


            return;
          }


          /*
           * Progressive delay:
           *
           * ~1.25s
           * ~2.0s
           * ~2.75s
           * ~3.5s
           * then max 4 seconds.
           *
           * Gives a fresh Quick Tunnel roughly one minute
           * to become reachable without hammering it.
           */
          const delay =
            Math.min(
              500 +
                consecutiveFailures *
                750,

              4000
            );


          retryTimer =
            setTimeout(
              connect,
              delay
            );
        };


      const connect =
        () => {

          if (
            disposed
          ) {

            return;
          }


          let opened =
            false;


          const socket =
            new WebSocket(
              socketUrl
            );


          websocketRef.current =
            socket;


          /*
           * Receive the native JPEG payload as raw bytes.
           * The desktop host has been independently verified
           * to stream complete FF D8 FF JPEG messages locally
           * and through the public Cloudflare WebSocket.
           */
          socket.binaryType =
            'arraybuffer';


          socket.onopen =
            () => {

              if (
                disposed
              ) {

                try {

                  socket.close();

                }
                catch {}


                return;
              }


              opened =
                true;


              /*
               * A successful handshake resets the failure
               * sequence. If this connection drops later,
               * reconnect begins again from the short delay.
               */
              consecutiveFailures =
                0;


              /*
               * HTTP 101 / WebSocket OPEN only proves transport.
               * The viewer becomes LIVE after a real JPEG frame
               * has successfully decoded in the browser.
               */
              setStreamConnected(
                false
              );


              setError(
                null
              );
            };


          socket.onmessage =
            event => {

              if (
                disposed
              ) {

                return;
              }


              /*
               * live_view sends a small JSON hello message first:
               *
               * {"fps":8,"height":1080,"mode":"view-only",...}
               *
               * It is metadata, not a desktop frame.
               */
              if (
                typeof event.data ===
                  'string'
              ) {

                return;
              }


              if (
                !(
                  event.data instanceof
                    ArrayBuffer
                )
              ) {

                console.warn(
                  'KAIZEN Remote Desktop received an unexpected binary payload type.'
                );

                return;
              }


              const bytes =
                new Uint8Array(
                  event.data
                );


              /*
               * Native stream contract:
               * every binary message is a complete JPEG frame.
               */
              if (
                bytes.length < 3 ||
                bytes[0] !== 0xff ||
                bytes[1] !== 0xd8 ||
                bytes[2] !== 0xff
              ) {

                console.warn(
                  'KAIZEN Remote Desktop rejected a non-JPEG binary frame.'
                );

                return;
              }


              const image =
                imageRef.current;


              if (
                !image
              ) {

                return;
              }


              const frameBlob =
                new Blob(
                  [
                    event.data
                  ],
                  {
                    type:
                      'image/jpeg'
                  }
                );


              const nextUrl =
                URL.createObjectURL(
                  frameBlob
                );


              const previousUrl =
                previousFrameUrlRef.current;


              image.onload =
                () => {

                  /*
                   * A successfully decoded JPEG is the actual
                   * definition of a connected live desktop.
                   */
                  setStreamConnected(
                    true
                  );


                  setError(
                    null
                  );


                  if (
                    previousUrl
                  ) {

                    URL.revokeObjectURL(
                      previousUrl
                    );
                  }
                };


              image.onerror =
                () => {

                  URL.revokeObjectURL(
                    nextUrl
                  );


                  console.error(
                    'KAIZEN Remote Desktop could not decode the JPEG frame.'
                  );
                };


              previousFrameUrlRef.current =
                nextUrl;


              image.src =
                nextUrl;


              frameCounterRef.current++;


              const now =
                performance.now();


              const elapsed =
                now -
                frameTimerRef.current;


              if (
                elapsed >=
                  1000
              ) {

                const fps =
                  Math.round(
                    (
                      frameCounterRef.current *
                      1000
                    ) /
                    elapsed
                  );


                setDisplayFps(
                  fps
                );


                frameCounterRef.current =
                  0;


                frameTimerRef.current =
                  now;
              }
            };


          socket.onerror =
            () => {

              /*
               * Browser WebSocket errors contain almost no
               * useful detail. Let onclose drive recovery.
               *
               * Importantly: do NOT immediately show the
               * previous scary red error banner.
               */
              try {

                socket.close();

              }
              catch {}
            };


          socket.onclose =
            () => {

              if (
                websocketRef.current ===
                  socket
              ) {

                websocketRef.current =
                  null;
              }


              setStreamConnected(
                false
              );


              if (
                disposed
              ) {

                return;
              }


              /*
               * If we had a working stream and then lost it,
               * begin a fresh reconnect sequence.
               */
              if (
                opened
              ) {

                consecutiveFailures =
                  0;
              }


              scheduleReconnect();
            };
        };


      /*
       * Small initial delay specifically prevents racing the
       * Cloudflare hostname announcement.
       */
      retryTimer =
        setTimeout(
          connect,
          1200
        );


      return () => {

        disposed =
          true;


        if (
          retryTimer
        ) {

          clearTimeout(
            retryTimer
          );
        }


        const socket =
          websocketRef.current;


        websocketRef.current =
          null;


        if (
          socket
        ) {

          try {

            socket.close();

          }
          catch {}
        }


        if (
          previousFrameUrlRef.current
        ) {

          URL.revokeObjectURL(
            previousFrameUrlRef.current
          );


          previousFrameUrlRef.current =
            null;
        }
      };
    },
    [
      session
    ]
  );

  // =========================================================
  // START SESSION
  // =========================================================

  const startSession =
    async () => {

      const channel =
        channelRef.current;


      if (
        !device ||
        !channel ||
        !online
      ) {

        setError(
          'KAIZEN Agent is not online.'
        );

        return;
      }


      setError(
        null
      );


      setActionState(
        'starting'
      );


      const requestId =
        crypto.randomUUID();


      const responsePromise =
        new Promise<any>(
          (
            resolve,
            reject
          ) => {

            const timer =
              setTimeout(
                () => {

                  pendingStarts.current.delete(
                    requestId
                  );


                  reject(
                    new Error(
                      'Remote Desktop startup timed out.'
                    )
                  );
                },
                75000
              );


            pendingStarts.current.set(
              requestId,
              {
                resolve,
                reject,
                timer
              }
            );
          }
        );


      try {

        const dispatch =
          await channel.send(
            {
              type:
                'broadcast',

              event:
                'remote_desktop_start_request',

              payload: {

                id:
                  `remote-desktop-start-${requestId}`,

                type:
                  'remote_desktop_start_request',

                source:
                  'web',

                targetId:
                  device.id,

                timestamp:
                  new Date()
                    .toISOString(),

                payload: {

                  requestId,

                  fps:
                    8
                }
              }
            }
          );


        if (
          dispatch !==
            'ok'
        ) {

          throw new Error(
            `Remote Desktop dispatch returned ${dispatch}`
          );
        }


        const result =
          await responsePromise;


        if (
          result?.success !==
            true ||
          !result?.session
        ) {

          throw new Error(
            result?.error ||
            'Remote Desktop session could not be started.'
          );
        }


        setSession(
          result.session
        );


        setActionState(
          'idle'
        );

      }
      catch (
        startError
      ) {

        setSession(
          null
        );


        setActionState(
          'idle'
        );


        setError(
          startError instanceof Error
            ? startError.message
            : String(
                startError
              )
        );
      }
    };


  // =========================================================
  // STOP SESSION
  // =========================================================

  const stopSession =
    async () => {

      const channel =
        channelRef.current;


      setActionState(
        'stopping'
      );


      if (
        !device ||
        !channel
      ) {

        setSession(
          null
        );


        setActionState(
          'idle'
        );


        return;
      }


      const requestId =
        crypto.randomUUID();


      const responsePromise =
        new Promise<any>(
          (
            resolve,
            reject
          ) => {

            const timer =
              setTimeout(
                () => {

                  pendingStops.current.delete(
                    requestId
                  );


                  reject(
                    new Error(
                      'Remote Desktop disconnect timed out.'
                    )
                  );
                },
                20000
              );


            pendingStops.current.set(
              requestId,
              {
                resolve,
                reject,
                timer
              }
            );
          }
        );


      try {

        const dispatch =
          await channel.send(
            {
              type:
                'broadcast',

              event:
                'remote_desktop_stop_request',

              payload: {

                id:
                  `remote-desktop-stop-${requestId}`,

                type:
                  'remote_desktop_stop_request',

                source:
                  'web',

                targetId:
                  device.id,

                timestamp:
                  new Date()
                    .toISOString(),

                payload: {
                  requestId
                }
              }
            }
          );


        if (
          dispatch ===
            'ok'
        ) {

          await responsePromise;
        }

      }
      catch {
        /*
         * Local viewer still closes.
         * The next Start also cleans stale native processes.
         */
      }


      setSession(
        null
      );


      setError(
        null
      );


      setActionState(
        'idle'
      );
    };


  const enterFullscreen =
    async () => {

      if (
        !viewerRef.current
      ) {

        return;
      }


      try {

        await viewerRef.current
          .requestFullscreen();

      }
      catch {

        setError(
          'Fullscreen is unavailable in this browser.'
        );
      }
    };


  return (

    <div
      className="
        space-y-4
        md:space-y-5
      "
    >

      {/* ==================================================
          HEADER
      ================================================== */}

      <div
        className="
          flex
          items-start
          justify-between
          gap-3
        "
      >

        <div
          className="
            flex
            items-center
            gap-2.5
            min-w-0
          "
        >

          <div
            className="
              w-8
              h-8
              shrink-0
              rounded-lg
              border
              border-indigo-400/15
              bg-indigo-400/[0.07]
              text-indigo-300
              flex
              items-center
              justify-center
            "
          >

            <MonitorUp
              className="
                w-4
                h-4
              "
            />

          </div>


          <div
            className="
              min-w-0
            "
          >

            <h1
              className="
                text-sm
                md:text-[15px]
                font-semibold
                text-slate-100
              "
            >
              Remote Desktop
            </h1>


            <div
              className="
                mt-0.5
                text-[10px]
                text-slate-500
              "
            >
              Live Windows workspace
            </div>

          </div>

        </div>


        <div
          className={`
            shrink-0
            h-7
            px-2.5
            rounded-lg
            border
            flex
            items-center
            gap-1.5
            text-[9px]
            font-semibold
            uppercase
            tracking-[0.12em]
            ${
              session &&
              streamConnected
                ? `
                    border-emerald-400/20
                    bg-emerald-400/[0.07]
                    text-emerald-300
                  `
                : `
                    border-white/[0.07]
                    bg-white/[0.025]
                    text-slate-500
                  `
            }
          `}
        >

          <Radio
            className="
              w-3
              h-3
            "
          />


          {session &&
          streamConnected
            ? 'Live'
            : 'Standby'}

        </div>

      </div>


      {/* ==================================================
          HOST STATUS
      ================================================== */}

      <div
        className="
          rounded-xl
          border
          border-white/[0.07]
          bg-white/[0.025]
          px-3.5
          py-3
          flex
          flex-wrap
          items-center
          justify-between
          gap-3
        "
      >

        <div
          className="
            flex
            items-center
            gap-2.5
            min-w-0
          "
        >

          <span
            className={`
              w-2
              h-2
              shrink-0
              rounded-full
              ${
                online
                  ? 'bg-emerald-400'
                  : 'bg-rose-400'
              }
            `}
          />


          <div
            className="
              min-w-0
            "
          >

            <div
              className="
                text-[10px]
                font-medium
                text-slate-300
              "
            >
              {online
                ? 'Laptop Online'
                : 'Laptop Offline'}
            </div>


            <div
              className="
                mt-0.5
                text-[9px]
                font-mono
                text-slate-600
                truncate
              "
            >

              {device?.name ||
                'Windows Laptop'}


              {latencyMs !==
                null
                  ? ` · ${latencyMs}ms`
                  : ''}

            </div>

          </div>

        </div>


        <div
          className="
            flex
            items-center
            gap-1.5
            text-[9px]
            text-slate-600
          "
        >

          <ShieldCheck
            className="
              w-3
              h-3
              text-indigo-400
            "
          />


          {channelReady
            ? 'Control channel ready'
            : 'Control channel waiting'}

        </div>

      </div>


      {/* ==================================================
          DESKTOP VIEWER
      ================================================== */}

      <div
        ref={
          viewerRef
        }
        className="
          relative
          overflow-hidden
          rounded-[18px]
          border
          border-white/10
          bg-slate-900/82
          backdrop-blur-2xl
          shadow-[0_28px_90px_rgba(2,6,23,0.44)]
        "
      >

        {/* VIEWER TOOLBAR */}

        <div
          className="
            h-11
            px-3
            md:px-4
            flex
            items-center
            justify-between
            gap-3
            border-b
            border-white/[0.07]
            bg-white/[0.025]
          "
        >

          <div
            className="
              flex
              items-center
              gap-2.5
              min-w-0
            "
          >

            <div
              className="
                flex
                items-center
                gap-1.5
              "
              aria-hidden="true"
            >

              <span
                className="
                  w-2
                  h-2
                  rounded-full
                  bg-rose-400/80
                "
              />

              <span
                className="
                  w-2
                  h-2
                  rounded-full
                  bg-amber-400/80
                "
              />

              <span
                className="
                  w-2
                  h-2
                  rounded-full
                  bg-emerald-400/80
                "
              />

            </div>


            <span
              className="
                truncate
                font-mono
                text-[9px]
                text-slate-500
              "
            >

              {session
                ? `${session.width}×${session.height} · ${displayFps || 0} FPS · VIEW ONLY`
                : 'Windows Desktop'}

            </span>

          </div>


          {session && (

            <button
              type="button"
              onClick={
                enterFullscreen
              }
              className="
                h-8
                px-2.5
                rounded-lg
                border
                border-white/[0.07]
                bg-white/[0.025]
                text-slate-400
                hover:text-slate-100
                hover:bg-white/[0.05]
                transition-colors
                active:scale-[0.98]
                flex
                items-center
                gap-1.5
                text-[9px]
                font-medium
              "
            >

              <Expand
                className="
                  w-3
                  h-3
                "
              />

              Fullscreen

            </button>

          )}

        </div>


        {/* REAL DESKTOP */}

        <div
          className="
            relative
            min-h-[48dvh]
            sm:min-h-0
            sm:aspect-video
            bg-[#020817]
            flex
            items-center
            justify-center
            overflow-hidden
          "
        >

          <img
            ref={
              imageRef
            }
            alt="KAIZEN Remote Desktop"
            draggable={
              false
            }
            className={`
              absolute
              inset-0
              w-full
              h-full
              object-contain
              select-none
              bg-black
              ${
                session &&
                streamConnected
                  ? 'block'
                  : 'hidden'
              }
            `}
          />


          {!session && (

            <div
              className="
                max-w-sm
                px-6
                text-center
              "
            >

              <div
                className="
                  mx-auto
                  w-11
                  h-11
                  rounded-xl
                  border
                  border-indigo-400/15
                  bg-indigo-400/[0.07]
                  text-indigo-300
                  flex
                  items-center
                  justify-center
                "
              >

                <MonitorUp
                  className="
                    w-5
                    h-5
                  "
                />

              </div>


              <div
                className="
                  mt-3
                  text-xs
                  font-semibold
                  text-slate-300
                "
              >
                Windows workspace ready
              </div>


              <div
                className="
                  mt-1.5
                  text-[10px]
                  leading-5
                  text-slate-600
                "
              >
                Start a secure live viewing session.
                Landscape orientation is recommended on mobile.
              </div>

            </div>

          )}


          {session &&
          !streamConnected && (

            <div
              className="
                flex
                flex-col
                items-center
                gap-3
                text-center
              "
            >

              <Loader2
                className="
                  w-5
                  h-5
                  text-indigo-400
                  animate-spin
                "
              />


              <div
                className="
                  text-[10px]
                  font-mono
                  text-slate-500
                "
              >
                Connecting live desktop…
              </div>

            </div>

          )}

        </div>


        {/* FOOTER */}

        <div
          className="
            min-h-[58px]
            px-3
            py-3
            md:px-4
            border-t
            border-white/[0.07]
            bg-white/[0.025]
            flex
            items-center
            justify-between
            gap-3
          "
        >

          <div
            className="
              hidden
              sm:flex
              items-center
              gap-1.5
              text-[9px]
              text-slate-600
            "
          >

            <Wifi
              className="
                w-3
                h-3
              "
            />

            Live media is transient.

          </div>


          <div
            className="
              ml-auto
              flex
              items-center
              gap-2
            "
          >

            {!session ? (

              <button
                type="button"
                disabled={
                  !online ||
                  !channelReady ||
                  busy
                }
                onClick={
                  () =>
                    void startSession()
                }
                className="
                  min-h-[42px]
                  px-4
                  rounded-lg
                  border
                  border-indigo-400/20
                  bg-indigo-600
                  text-white
                  text-[10px]
                  font-semibold
                  shadow-[0_10px_30px_rgba(79,70,229,0.18)]
                  transition-all
                  active:scale-[0.98]
                  disabled:opacity-40
                  disabled:pointer-events-none
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >

                {actionState ===
                  'starting'
                    ? (
                      <Loader2
                        className="
                          w-3.5
                          h-3.5
                          animate-spin
                        "
                      />
                    )
                    : (
                      <Play
                        className="
                          w-3.5
                          h-3.5
                        "
                      />
                    )}


                {actionState ===
                  'starting'
                    ? 'Starting…'
                    : 'Start Session'}

              </button>

            ) : (

              <button
                type="button"
                disabled={
                  busy
                }
                onClick={
                  () =>
                    void stopSession()
                }
                className="
                  min-h-[42px]
                  px-4
                  rounded-lg
                  border
                  border-rose-400/20
                  bg-rose-400/[0.07]
                  text-rose-300
                  text-[10px]
                  font-semibold
                  hover:bg-rose-400/[0.11]
                  transition-colors
                  active:scale-[0.98]
                  disabled:opacity-40
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >

                {actionState ===
                  'stopping'
                    ? (
                      <Loader2
                        className="
                          w-3.5
                          h-3.5
                          animate-spin
                        "
                      />
                    )
                    : (
                      <Power
                        className="
                          w-3.5
                          h-3.5
                        "
                      />
                    )}


                {actionState ===
                  'stopping'
                    ? 'Disconnecting…'
                    : 'Disconnect'}

              </button>

            )}

          </div>

        </div>

      </div>


      {error && (

        <div
          className="
            rounded-xl
            border
            border-rose-400/15
            bg-rose-400/[0.07]
            px-3.5
            py-3
            text-[10px]
            text-rose-300
          "
        >
          {error}
        </div>

      )}

    </div>
  );
};
